import { useContext, useEffect } from 'react';

import { Flex, Skeleton } from 'antd';
import { useNavigate } from 'react-router-dom';

import { useGetCopilotSubscriptions } from '@/api/customer_subscription';
import { PageContext } from '@/providers/PageContextProvider';

import styles from './styles.module.css';

import SubscriptionCard from './SubscriptionCard';

type TPlan = {
	tier: string;
	price: string;
	amount: number;
	highlighted: boolean;
	id: number;
};

type TSubscription = {
	per_month_usd: string;
	subscription_name: string;
	item_count_per_month: number;
	id: number;
};

export const HIGHLIGHTED_VIDEOS_AMOUNT = 200;
export const COPILOT_TIERS = {
	100: 'Starter',
	200: 'Team',
	500: 'Enterprise',
};

export default function CopilotPage() {
	const navigate = useNavigate();
	const { setIsCreditsCounterVisible, setPageTitle } = useContext(PageContext);

	const { data, isLoading } = useGetCopilotSubscriptions();

	useEffect(() => {
		setPageTitle('Video Insights Co-Pilot');
		setIsCreditsCounterVisible(false);
	}, [setPageTitle, setIsCreditsCounterVisible]);

	const prices = (data?.data || [])
		?.filter(
			(subs: TSubscription) => !subs?.subscription_name?.includes('(1$)'),
		)
		?.map((subs: TSubscription) => {
			return {
				tier:
					COPILOT_TIERS[
						subs.item_count_per_month as keyof typeof COPILOT_TIERS
					] || subs.subscription_name,
				amount: subs.item_count_per_month,
				price: `$${subs.per_month_usd}`,
				highlighted: subs.item_count_per_month === HIGHLIGHTED_VIDEOS_AMOUNT,
				id: subs?.id,
			};
		});

	const onSubscribeClick = (id: number) => {
		const searchedSubscription = data?.data.find(
			(subs: TSubscription) => subs?.id === id,
		);

		navigate('/app/copilot-subscriptions/subscribe', {
			state: { subscription: searchedSubscription },
		});
	};

	if (isLoading) {
		return (
			<div className={styles.container}>
				<Flex
					style={{ width: '100%', marginBottom: '32px' }}
					align="center"
					justify="center"
					gap={24}
					vertical
				>
					<Skeleton.Node active style={{ width: 400, height: 30 }} />
					<Skeleton.Node active style={{ width: 250, height: 20 }} />
				</Flex>
				<Flex
					gap="40px"
					wrap="wrap"
					style={{ width: '100%' }}
					align="center"
					justify="center"
				>
					<Skeleton.Node active style={{ width: 240, height: 300 }} />
					<Skeleton.Node active style={{ width: 240, height: 300 }} />
					<Skeleton.Node active style={{ width: 240, height: 300 }} />
				</Flex>
			</div>
		);
	}

	return (
		<>
			<div className={styles.container}>
				<h1
					style={{
						textAlign: 'center',
						marginTop: 0,
						marginBottom: '12px',
						color: '#172b6f',
					}}
				>
					Video Insights Co-Pilot{' '}
				</h1>
				<h3
					style={{
						textAlign: 'center',
						marginTop: 0,
						marginBottom: '24px',
						color: '#777',
					}}
				>
					Choose your subscription plan
				</h3>
				<div className={styles.listWrapper}>
					<ul className={styles.cardsWrapper}>
						{prices.map((plan: TPlan) => (
							<SubscriptionCard
								key={plan.tier}
								plan={plan}
								onSubscribeClick={onSubscribeClick}
							/>
						))}
					</ul>
				</div>
			</div>
		</>
	);
}
